import React, { useEffect, useMemo, useState } from "react";

import { Box, Typography, UIButton, FormControl, Alert } from "@ui";
import { TextField } from "@mui/material";

import ThemeSelect from "@/ui/fields/Select";

import { httpGet, httpPost } from "@/shared/api/http";
import { customerEndpoints, sharePageEndpoints } from "@/shared/api/endpoints";
import { femaCapture } from "@/shared/fema/capture";
import { getCustomerId } from "@/shared/lib/utils/getCustomerId";
import type { DigitalLender } from "@/shared/api/types";
import SpecialTextField from "@/ui/fields/SpecialTextField";
import { ThemeColours } from "@/clients/acme/config/colours";
import { useAlert } from "@/app/providers/AlertProvider";
import { UI_MESSAGES } from "@/constants/ui";
import { useNavigate } from "react-router-dom";
import { useReferenceIdHelp } from "@/components/referenceId";
import { Company } from "@/pages/private/dashboard/SharePage";

const DEFAULT_COVERING_NOTE =
  "I am in financial difficulty and because of this I have used IE Hub to review my budget. I have enclosed a copy for your record. You will be able to see from this information that I'm unable to make my full contractual payments to you.";

interface ServiceEnvelope<T> {
  success?: boolean;
  data?: T;
}

type ServiceResponse<T> = ServiceEnvelope<T> | T;

function isServiceEnvelope<T>(
  response: ServiceResponse<T>
): response is ServiceEnvelope<T> {
  return (
    typeof response === "object" && response !== null && "data" in response
  );
}

export interface FreepostFormProps {
  onBack: () => void;
  companies?: Company[];
}

interface FieldErrors {
  companyName?: string | undefined;
  referenceId?: string | undefined;
  address?: string | undefined;
}

const FreepostForm: React.FC<FreepostFormProps> = ({ onBack, companies }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [noReferenceId, setNoReferenceId] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const [freepostCompanies, setFreepostCompanies] = useState<DigitalLender[]>(
    []
  );
  const [companyName, setCompanyName] = useState<string>("");
  const [referenceId, setReferenceId] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [coveringNote, setCoveringNote] = useState<string>(
    DEFAULT_COVERING_NOTE
  );

  const {
    helpDialog: freepostReferenceHelpDialog,
    helpLinkText: freepostReferenceHelpLinkText,
    label: freepostReferenceLabel,
    noValueCheckboxLabel: freepostNoValueCheckboxLabel,
  } = useReferenceIdHelp({ companyName });

  const resolvedNoValueCheckboxLabel =
    freepostNoValueCheckboxLabel ?? "I don't have this information";

  // Personalize covering note with user's mobile number if present
  useEffect(() => {
    try {
      const customerDataStr = localStorage.getItem("customerData");
      if (customerDataStr) {
        const customerData = JSON.parse(customerDataStr);
        const mobile: string | undefined = customerData?.mobileNumber;
        if (mobile) {
          setCoveringNote(`${DEFAULT_COVERING_NOTE} `);
        }
      }
    } catch {
      // ignore JSON/LS errors and keep default note
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const response = await httpGet<ServiceResponse<DigitalLender[]>>(
          customerEndpoints.listFreepostLenders
        );

        let data: DigitalLender[] = [];

        if (
          isServiceEnvelope<DigitalLender[]>(response.data) &&
          Array.isArray(response.data.data)
        ) {
          data = response.data.data ?? [];
        } else if (Array.isArray(response.data)) {
          data = response.data;
        }

        if (mounted) {
          setFreepostCompanies((data || []).filter(Boolean));
        }
      } catch (err) {
        setError("Failed to load Freepost companies.");
        femaCapture("freepost_company_list_failed", err, {
          category: "api",
          severity: "warning",
        });
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void fetchCompanies();

    return () => {
      mounted = false;
    };
  }, []);

  const companyOptions = useMemo(() => {
    const names = new Set<string>();
    for (const entry of freepostCompanies) {
      if (entry?.creditor) {
        names.add(entry.creditor);
      }
    }
    return Array.from(names);
  }, [freepostCompanies]);

  const handleCompanyChange = (value: string) => {
    setCompanyName(value);
    setFieldErrors((prev: FieldErrors) => ({
      ...prev,
      companyName: undefined,
    }));
    const found = freepostCompanies.find((c) => c.creditor === value);
    if (found?.creditorAddress) {
      setAddress(found.creditorAddress);
      setFieldErrors((prev: FieldErrors) => ({ ...prev, address: undefined }));
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress(e.target.value);
    setFieldErrors((prev: FieldErrors) => ({ ...prev, address: undefined }));
  };

  const validateFields = (): FieldErrors => {
    const errors: FieldErrors = {};

    if (!companyName.trim()) {
      errors.companyName = "Company name is required";
    }

    // Reference number is now always mandatory
    if (!referenceId.trim() && !noReferenceId) {
      errors.referenceId = "Reference number is required";
    }

    if (!address.trim()) {
      errors.address = "Address is required";
    }

    return errors;
  };

  const handleSubmit = async () => {
    // Validate fields first
    const errors = validateFields();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const resolvedCustomerId = await getCustomerId();
      if (!resolvedCustomerId) {
        setError("Missing customer context. Please sign in again.");
        return;
      }

      let userId: number | undefined;
      let emailAddress = null;
      try {
        const customerDataStr = localStorage.getItem("customerData");
        if (customerDataStr) {
          const customerData = JSON.parse(customerDataStr);
          if (customerData?.userId) {
            userId = Number(customerData.userId);
          }

          if (customerData?.emailAddress) {
            emailAddress = customerData.emailAddress;
          }
        }
      } catch {
        // ignore JSON/LS errors; userId stays undefined
      }

      const referenceNumber = referenceId || emailAddress;

      if (companies) {
        for (const company of companies) {
          if (
            company.reference == referenceNumber &&
            company.name == companyName
          ) {
            showAlert({
              severity: "warning",
              message: `A company with the same reference number already exists`,
              autoHide: true,
              duration: 2000,
            });

            setSubmitting(false);

            return;
          }
        }
      }

      const payload = {
        companyName,
        referenceNumber,
        customerId: Number(resolvedCustomerId),
        address: address.trim() || undefined,
        notes: coveringNote.trim() || undefined,
        shareNow: "N" as const,
        isTcAccepted: "Y" as const,
        isApiCall: "Y" as const,
        userId,
      };

      await httpPost(sharePageEndpoints.createFreepostSharing(), payload);

      setSuccess(
        "Added to Freepost queue. We will print and post your budget."
      );

      showAlert({
        severity: "success",
        message: "Added to Freepost queue. We will print and post your budget.",
        autoHide: true,
        duration: 5000,
      });

      navigate(-1);
    } catch (err) {
      const message =
        typeof (err as { message?: unknown })?.message === "string"
          ? (err as { message: string }).message
          : "Failed to create Freepost request.";
      setError(message);
      femaCapture("freepost_create_failed", err, {
        category: "api",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box data-testid="components-freepost-form-container-action-1" sx={{ pt: 1 }}>
      {error && (
        <Box data-testid="components-freepost-form-container-action-2" sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {success && (
        <Box data-testid="components-freepost-form-container-action-3" sx={{ mb: 2 }}>
          <Alert severity="success">{success}</Alert>
        </Box>
      )}

      <Box data-testid="components-freepost-form-container-action-4" sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Box data-testid="components-freepost-form-container-action-5">
          <Typography data-testid="components-freepost-form-text-company-name-*"
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
              fontSize: "16px",
              color: ThemeColours.label,
            }}
          >
            Company name *
          </Typography>
          <FormControl
            fullWidth
            variant="outlined"
            error={!!fieldErrors.companyName}
          >
            <ThemeSelect
              dialogLabel="Select a Company"
              options={[
                ...companyOptions.map((name) => ({
                  value: name,
                  label: name,
                })),
              ]}
              value={companyName}
              onChange={(value) => handleCompanyChange(String(value))}
              placeholder="Select a company"
              showErrorBelow={false}
              searchable
              fullWidth
              disabled={loading}
              error={!!fieldErrors.companyName}
              dataPrivate={false}
            />
            {fieldErrors.companyName && (
              <Typography data-testid="components-freepost-form-text-action" sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                {fieldErrors.companyName}
              </Typography>
            )}
          </FormControl>
        </Box>

        <Box data-testid="components-freepost-form-container-action-6">
          <SpecialTextField
            isAccountField={true}
            noValueChecked={noReferenceId}
            label={freepostReferenceLabel}
            placeholder="Enter your account number"
            value={referenceId}
            onChange={(e) => {
              setReferenceId(e.target.value);
              setFieldErrors((prev: FieldErrors) => ({
                ...prev,
                referenceId: undefined,
              }));
            }}
            onNoValueChange={(checked) => {
              setNoReferenceId(checked);
            }}
            // Reference number must be provided, so we no longer allow "no value"
            allowNoValue={true}
            noValueCheckboxLabel={resolvedNoValueCheckboxLabel}
            noValueFieldName="noReferenceId"
            helpLinkText={freepostReferenceHelpLinkText}
            helpDialog={freepostReferenceHelpDialog}
            showErrorBelow={true}
            error={!!fieldErrors.referenceId}
            helperText={fieldErrors.referenceId}
          />
        </Box>

        <Box data-testid="components-freepost-form-container-action-7">
          <Typography data-testid="components-freepost-form-text-address-*"
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
              fontSize: "16px",
              color: ThemeColours.label,
            }}
          >
            Address *
          </Typography>
          <TextField data-testid="components-freepost-form-input-company-postal-address"
            value={address}
            onChange={handleAddressChange}
            fullWidth
            multiline
            rows={3}
            placeholder="Company postal address"
            error={!!fieldErrors.address}
            helperText={fieldErrors.address}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "white",
              },
            }}
          />
        </Box>

        <Box data-testid="components-freepost-form-container-action-8">
          <Typography data-testid="components-freepost-form-text-covering-note-(optional)"
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
              fontSize: "16px",
              color: ThemeColours.label,
            }}
          >
            Covering Note (optional)
          </Typography>
          <TextField data-testid="components-freepost-form-input-add-an-optional-covering-note"
            value={coveringNote}
            onKeyDown={(e) => {
              if (
                coveringNote.length >= 500 &&
                e.key.length === 1 &&
                !e.ctrlKey &&
                !e.metaKey
              ) {
                showAlert({
                  severity: "warning",
                  message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                });
              }
            }}
            onPaste={(e) => {
              const pastedText = e.clipboardData.getData("text");
              const selectionStart = (e.target as any).selectionStart || 0;
              const selectionEnd = (e.target as any).selectionEnd || 0;
              const currentLength = coveringNote.length;
              if (
                currentLength - (selectionEnd - selectionStart) + pastedText.length >
                500
              ) {
                showAlert({
                  severity: "warning",
                  message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                });
              }
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCoveringNote(e.target.value)
            }
            fullWidth
            multiline
            rows={4}
            inputProps={{
              maxLength: 500,
            }}
            placeholder="Add an optional covering note"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "white",
              },
            }}
          />
        </Box>

        <Box data-testid="components-freepost-form-container-action-9"
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <UIButton data-testid="components-freepost-form-btn-on-back"
            onClick={onBack}
            sx={{
              height: "45px",
              backgroundColor: "#f3f4f6",
              borderRadius: "10px",
              color: "#1f2937",
              fontSize: "18px",
              fontWeight: 600,
              textTransform: "none",
              border: "none",
              "&:hover": {
                backgroundColor: "#e5e7eb",
                border: "none",
              },
              "&:disabled": {
                backgroundColor: "#f3f4f6",
                color: "#9ca3af",
              },
              minWidth: 100,
            }}
          >
            Back
          </UIButton>
          <UIButton data-testid="components-freepost-form-btn-handle-submit"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              height: "45px",
              backgroundColor: "#D4E03B",
              borderRadius: "10px",
              color: "#003838",
              fontSize: "18px",
              fontWeight: 600,
              textTransform: "none",
              border: "none",
              "&:hover": {
                backgroundColor: "#e6f332",
                border: "none",
              },
              minWidth: 200,
            }}
          >
            {submitting ? "Saving..." : "Save"}
          </UIButton>
        </Box>
      </Box>
    </Box>
  );
};

export default FreepostForm;
